import VueRouter, { RawLocation, Route } from 'vue-router';
import { Location } from './Stores/UserStore';
import store from '@/store';

enum ParamTypes {
    Query,
    Path,
    Prop,
    HardCoded
    // Cookie
}

interface ParamMap {
    sourceType: (ParamTypes.Query | ParamTypes.HardCoded);
    sourceName: string;
    hardCodedSourceValue?: string; // temp while only deploying select permitvats pages
    destName: string;
    destType: (ParamTypes.Prop | ParamTypes.Query);
    transform?(originalValue: string): Promise<string>;
}

interface RouteMap {
    params: ParamMap[];
    routeName: string;
}

const locationDBQueryProp: ParamMap = {
    sourceType: ParamTypes.Query,
    sourceName: 'locationDBName',
    destName: 'location',
    destType: ParamTypes.Prop,
    transform: async (databaseName: string) => {
        const locations = await store.dispatch('userModule/getAllowedLocations') as Location[];
        const location = locations
            .find(item => item.databaseName === databaseName);
        if (location) {
            return location.urlSegment;
        }
        throw new Error(`Unknown location parent id ${databaseName}`);
    }
};

const defaultLocationQueryProp: ParamMap = {
    sourceType: ParamTypes.Query,
    sourceName: 'location',
    destName: 'location',
    destType: ParamTypes.Prop,
    transform: async (originalValue: string) => {
        const locations = await store.dispatch('userModule/getAllowedLocations') as Location[];
        const numberPattern = new RegExp(/[0-9]+/g);
        const searchResult = numberPattern.exec(originalValue);
        if (!searchResult || !searchResult.length) {
            throw new Error(`No numeric value found in 'location' query param '${originalValue}'`);
        }
        const numericValue = Number.parseInt(searchResult[0], 10);
        const location = locations
            .find(item => item.locationParentID === numericValue);
        if (location) {
            return location.urlSegment;
        }
        throw new Error(`Unknown location parent id ${originalValue} (numeric value ${numericValue})`);
    }
};

export interface MenuItem {
    recId: number;
    menuId?: number;
    text: string;
    navigateUrl: string | RawLocation;
    children: MenuItem[];
}

const routeMaps: { [path: string]: RouteMap } = {
    '/main/search.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp,
            {
                sourceName: 'plate',
                sourceType: ParamTypes.Query,
                destName: 'plate',
                destType: ParamTypes.Query
            }
        ],
        routeName: 'citationSearch'
    },
    '/admin/appealhearinglettertypemgmt.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'letterMatchAdmin'
    },
    '/admin/autoreportsmaintenance.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'autoReportsMaintenance'
    },
    '/admin/bootmanagement.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'bootManagementAdmin'
    },
    '/admin/createreleasenotes.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'releaseNotesAdmin'
    },
    '/admin/customeralias.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'customerAlias'
    },
    '/admin/HandheldUserMgmt.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'handheldUserAdmin'
    },
    '/admin/HandheldMessages.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'handheldMessage'
    },
    '/admin/LotAreaManagement.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'lotAreaAdmin'
    },
    '/main/IssueTickets.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'issueTicket'
    },
    '/main/lprtracking.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'lprAnalysis'
    },
    '/main/VATSTracking.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'vatsTracking'
    },
    '/admin/LotMaintenance.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'lotMaintenance',
    },
    '/admin/HearingDateAdmin.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'hearingDatesAdmin'
    },
    '/admin/MappingMeterMgmt.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'meterMapping'
    },
    '/admin/MyReportsManagement.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'myReportsManagement'
    },
    '/admin/usermgmt.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'userManagementAdmin'
    },
    '/admin/notificationmgmt.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'notificationManagementAdmin'
    },
    '/admin/systemvariables.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'programVariables'
    },
    '/main/appeals.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'AppealRoot'
    },
    '/admin/ChalkingLocationRules.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'chalkingLocationRules'
    },
    '/admin/diagnosticcounts.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'diagnosticCountsAdmin'
    },
    '/main/permits.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'permitPurchase'
    },
    '/permits': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'permitPurchaseNoLocation'
    },
    '/permits/default.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'permitPurchaseNoLocation'
    },
    '/permits/payment.aspx': {
        params: [
            defaultLocationQueryProp,
            {
                sourceName: 'permitNumber',
                sourceType: ParamTypes.Query,
                destName: 'permitID',
                destType: ParamTypes.Prop
            }
        ],
        routeName: 'permitPayment'
    },
    '/permits/TownOfBarnstable.aspx': {
        params: [
            {
                sourceName: '',
                sourceType: ParamTypes.HardCoded,
                hardCodedSourceValue: 'Barnstable',
                destName: 'location',
                destType: ParamTypes.Prop
            }
        ],
        routeName: 'permitPurchase'
    },
    '/permits/Bangor.aspx': {
        params: [
            {
                sourceName: '',
                sourceType: ParamTypes.HardCoded,
                hardCodedSourceValue: 'Bangor',
                destName: 'location',
                destType: ParamTypes.Prop
            }
        ],
        routeName: 'permitPurchase'
    },
    '/permits/CityOfDurham.aspx': {
        params: [
            {
                sourceName: '',
                sourceType: ParamTypes.HardCoded,
                hardCodedSourceValue: 'Durham',
                destName: 'location',
                destType: ParamTypes.Prop
            }
        ],
        routeName: 'permitPurchase'
    },
    '/permits/CityOfMedford.aspx': {
        params: [
            {
                sourceName: '',
                sourceType: ParamTypes.HardCoded,
                hardCodedSourceValue: 'Medford',
                destName: 'location',
                destType: ParamTypes.Prop
            }
        ],
        routeName: 'permitPurchase'
    },
    '/permits/Mobile.aspx': {
        params: [
            {
                sourceName: '',
                sourceType: ParamTypes.HardCoded,
                hardCodedSourceValue: 'Mobile',
                destName: 'location',
                destType: ParamTypes.Prop
            }
        ],
        routeName: 'permitPurchase'
    },
    '/permits/AirfieldPermit.aspx': {
        params: [
            {
                sourceName: '',
                sourceType: ParamTypes.HardCoded,
                hardCodedSourceValue: 'FairbanksAirport',
                destName: 'location',
                destType: ParamTypes.Prop
            }
        ],
        routeName: 'permitPurchase'
    },
    '/admin/tablemgmt.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'tableManagementAdmin'
    },
    '/main/reportinggenerator.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'reportingGenerator'
    },
    '/admin/lettermgmt.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'letterManagement'
    },
    '/admin/permittypemgmt.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'permitManagement'
    },
    '/admin/CustomerThirdPartyFileHistory.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp,
            {
                sourceName: 'fileId',
                sourceType: ParamTypes.Query,
                destName: 'fileId',
                destType: ParamTypes.Prop
            },
        ],
        routeName: 'customerFileHistory'
    },
    '/admin/bulkholdrequest.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'bulkholdrequest'
    },
    '/admin/reversalreasonmgmt.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'reversalReasonManagement'
    },
    'admin/vendor-lot-ids': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'vendorLotIds'
    },
    '/admin/reportmgmt.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'reportManagement'
    },
    '/payments/payvats.aspx': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp,
            {
                sourceName: 'recid',
                sourceType: ParamTypes.Query,
                destName: 'citation',
                destType: ParamTypes.Query
            },
            {
                sourceName: 'plate',
                sourceType: ParamTypes.Query,
                destName: 'plate',
                destType: ParamTypes.Query
            },
            {
                sourceName: 'state',
                sourceType: ParamTypes.Query,
                destName: 'state',
                destType: ParamTypes.Query
            }
        ],
        routeName: 'citationPayment'
    },
    '/payments/default.aspx': {
        params: [
            locationDBQueryProp,
            {
                sourceName: 'recid',
                sourceType: ParamTypes.Query,
                destName: 'citation',
                destType: ParamTypes.Query
            },
            {
                sourceName: 'plate',
                sourceType: ParamTypes.Query,
                destName: 'plate',
                destType: ParamTypes.Query
            },
            {
                sourceName: 'state',
                sourceType: ParamTypes.Query,
                destName: 'state',
                destType: ParamTypes.Query
            }
        ],
        routeName: 'citationPayment'
    },
    '/payments': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp,
            {
                sourceName: 'recid',
                sourceType: ParamTypes.Query,
                destName: 'citation',
                destType: ParamTypes.Query
            },
            {
                sourceName: 'plate',
                sourceType: ParamTypes.Query,
                destName: 'plate',
                destType: ParamTypes.Query
            },
            {
                sourceName: 'state',
                sourceType: ParamTypes.Query,
                destName: 'state',
                destType: ParamTypes.Query
            }
        ],
        routeName: 'citationPayment',
    },
    'admin/text-overrides': {
        params: [
            locationDBQueryProp,
            defaultLocationQueryProp
        ],
        routeName: 'localizationOverrides'
    }
};

for (const url in routeMaps) {
    if (routeMaps[url]) {
        const mapping = routeMaps[url];
        delete routeMaps[url];
        routeMaps[url.toUpperCase()] = mapping;
        const queryParamMaps = mapping.params.filter(item => item.sourceType === ParamTypes.Query);
        for (const param of queryParamMaps) {
            param.sourceName = param.sourceName.toUpperCase();
        }
    }
}

const getCookieValue = (cookieName: string): string => {
    const cookieVal = document.cookie.match(`(^|;) ?${cookieName}=([^;]*)(;|$)`);
    if (cookieVal) {
        return cookieVal[2];
    }
    return '';
};

const mapToRoute = async (map: RouteMap, route: Route): Promise<RawLocation> => {
    const newParams: { [paramName: string]: any } = {};
    const newQueryParams: { [paramName: string]: string } = {};

    const mapParam = async (paramMap: ParamMap, sourceValue: string) => {
        const transform = paramMap.transform || ((original: string) => Promise.resolve(original));
        if (sourceValue) {
            if (paramMap.destType === ParamTypes.Query) {
                newQueryParams[paramMap.destName] = await transform(sourceValue);
            }
            else if (paramMap.destType === ParamTypes.Prop) {
                newParams[paramMap.destName] = await transform(sourceValue);
            }
        }
    };

    const routeQueryParams = { ...route.query };
    for (const queryId in routeQueryParams) {
        if (!routeQueryParams[queryId]) {
            continue;
        }
        const value = routeQueryParams[queryId];
        delete routeQueryParams[queryId];
        routeQueryParams[queryId.toUpperCase()] = value;
    }

    for (const paramMap of map.params) {
        if (paramMap.sourceType === ParamTypes.Query) {
            // Type cast is kind of a hack, but I don't think we ever pass arrays as query params in VATS.
            // Query params are not considered case-sensitive.
            const sourceValue = routeQueryParams[paramMap.sourceName.toUpperCase()] as string;
            await mapParam(paramMap, sourceValue);
        }
        else if (paramMap.sourceType === ParamTypes.HardCoded
            && paramMap.hardCodedSourceValue != null) {
            const sourceValue = paramMap.hardCodedSourceValue;
            await mapParam(paramMap, sourceValue);
        }
    }

    return { name: map.routeName, params: newParams, query: newQueryParams };
};

const host = window.location.hostname.toLowerCase();
const validMenuMappingHosts = ['localhost', 'development-core.municipalcitationsolutions.com'];
const performMenuMapping = validMenuMappingHosts.find(validHost => validHost === host) !== undefined;

export default {
    async mapAndReroute(router: VueRouter) {
        let path = window.location.pathname.toUpperCase();
        // Trim trailing slash so we catch requests for directories' default documents
        if (path.endsWith('/')) {
            path = path.substring(0, path.length - 1);
        }
        const map = routeMaps[path];
        if (map) {
            const fullPath = router.currentRoute.fullPath === '/'
                ? (router as any).history?.pending || router.currentRoute
                : router.currentRoute;
            const existingRoute = router.resolve(path);

            const mappedRoute = await mapToRoute(map, fullPath);
            if (!existingRoute.resolved.matched.length
                || !existingRoute.resolved.matched.some(route => route.path.toUpperCase() === path)) {
                router.addRoutes([
                    {
                        path,
                        redirect: () => {
                            return router.resolve(mappedRoute).location;
                        }
                    }
                ]);
            }

            router.push(mappedRoute);
        }
    },
    async mapMenu(menuItem: MenuItem, router: VueRouter, location: Location) {
        // Not a string, already mapped
        if (!(typeof menuItem.navigateUrl === 'string')) {
            if (menuItem.navigateUrl.params &&
                menuItem.navigateUrl.params.location) {
                menuItem.navigateUrl.params.location = location.urlSegment;
            }
            return menuItem;
        }

        // Old ASP.NET treated '~' as a special character, equivalent to application root
        // Nothing else does, so we need to clean it
        menuItem.navigateUrl = (menuItem.navigateUrl || '').replace('~/', '/');

        const map = routeMaps[menuItem.navigateUrl.toUpperCase()];

        if (map && performMenuMapping) {
            let additionalProps = {};
            // Use provided location instead of cookie
            // If no location required or provided, fallback to default behavior
            if (map.params.find(item => item === locationDBQueryProp) && location.urlSegment) {
                additionalProps = {
                    [locationDBQueryProp.destName]: location.urlSegment
                };
            }
            const params = await mapToRoute(map, router.currentRoute);
            // Prevent query params from being embedded into mapped menu items for all time
            (params as any).query = {};
            const newMenuItem = Object.assign({}, menuItem, {
                navigateUrl: Object.assign({}, params, { params: additionalProps })
            });
            return newMenuItem;
        }

        return menuItem;
    }
};
